<template>
  <div id="tour-teacher-details-page" class="space-y-3 mb-4 m-3" data-testid="teacher-details-page">
    <div class="flex items-center gap-2">
      <Button size="small" icon="pi pi-arrow-left" severity="secondary" text rounded @click="goBack" data-testid="btn-teacher-back" />
      <div>
        <h1 class="text-lg font-semibold text-gray-800">Staff Details</h1>
        <p class="text-xs text-gray-500 mt-0.5">View and manage staff information</p>
      </div>
    </div>

    <template v-if="loading">
      <div class="flex items-center justify-center py-12">
        <span class="pi pi-spin pi-spinner text-xl text-primary"></span>
      </div>
    </template>

    <template v-else-if="!teacher">
      <div class="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-center" data-testid="teacher-not-found">
        <i class="pi pi-user text-2xl text-yellow-400 mb-2"></i>
        <p class="text-yellow-800 font-medium">Staff member not found.</p>
      </div>
    </template>

    <template v-else>
      <TeacherProfileCard :teacher="teacher" />

      <EntityCustomFields :entity="CustomFieldEntity.Staff" :entityId="teacherId" :canEdit="canEditStaff" />

      <StaffDepartmentCard v-if="isTertiarySchool" :teacher="teacher" @updated="refetch()" />

      <LecturerCoursesWidget v-if="isTertiarySchool" :staff-id="teacherId" />

      <TeacherSubjectsCard :subjects="teacher.subjects" />

      <RequestRoleDialog v-model:visible="showRequestRoleDialog" @submitted="refetch()" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable';
import type { Staff } from '@/types/composition-functions';
import { GET_STAFF_BY_ID } from '@/graphql';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import TeacherProfileCard from './TeacherProfileCard.vue';
import StaffDepartmentCard from './StaffDepartmentCard.vue';
import LecturerCoursesWidget from './LecturerCoursesWidget.vue';
import TeacherSubjectsCard from './TeacherSubjectsCard.vue';
import RequestRoleDialog from './RequestRoleDialog.vue';
import EntityCustomFields from '@/components/common/EntityCustomFields.vue';
import { CustomFieldEntity } from '@/types/composition-functions';
import { useUserStore } from '@/store/userStore';
import { AppPermissions, can } from '@/utils/permissions';

const route = useRoute();
const router = useRouter();
const { isTertiary: isTertiarySchool } = useSchoolFeatures();

const teacherId = computed(() => route.params.id as string);
const showRequestRoleDialog = ref(false);

const userStore = useUserStore();
const canEditStaff = computed(() =>
  can(userStore.currentUser, AppPermissions.Teachers.Actions.Update, AppPermissions.Teachers.Resource),
);

const { result: staffResult, loading, refetch } = useQuery(
  GET_STAFF_BY_ID,
  () => ({ id: teacherId.value }),
  { fetchPolicy: 'network-only' },
);

const teacher = computed<Staff | null>(() => staffResult.value?.staff ?? null);

const goBack = () => router.back();
</script>
